import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Bell, Webhook, Mail, TriangleAlert, Check, RefreshCw } from 'lucide-react';
import { portalBillingAlertsApi } from '../../services/api';
import { toast } from '../../stores';
import { Eyebrow } from '../instrument';

const BAND_STYLES = {
  50:  { color: '#5790C9', bg: 'rgba(87,144,201,0.12)',  border: 'rgba(87,144,201,0.3)',  label: 'info' },
  80:  { color: '#F26B1F', bg: 'rgba(242,107,31,0.12)',  border: 'rgba(242,107,31,0.3)',  label: 'warning' },
  95:  { color: '#F26B1F', bg: 'rgba(242,107,31,0.18)',  border: 'rgba(242,107,31,0.4)',  label: 'urgent' },
  100: { color: '#7A1F1F', bg: 'rgba(122,31,31,0.20)',   border: 'rgba(122,31,31,0.5)',   label: 'over limit' },
};

export const BillingAlertsPanel = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['billing-alerts'],
    queryFn: () => portalBillingAlertsApi.list().then((r) => r.data),
    staleTime: 15000,
  });
  const [webhook, setWebhook] = useState('');
  const [emailOn, setEmailOn] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Hydrate form when data arrives
  if (!initialized && data?.config) {
    setInitialized(true);
    setWebhook(data.config.webhook_url || '');
    setEmailOn(data.config.email_notifications !== false);
  }

  const saveMut = useMutation({
    mutationFn: (body) => portalBillingAlertsApi.saveConfig(body),
    onSuccess: () => { toast.success('Alert preferences saved'); qc.invalidateQueries({ queryKey: ['billing-alerts'] }); },
    onError: () => toast.error('Failed to save'),
  });
  const checkMut = useMutation({
    mutationFn: () => portalBillingAlertsApi.check(),
    onSuccess: (res) => {
      toast.success(`Checked — ${res.data.percent_used}% of quota used`);
      qc.invalidateQueries({ queryKey: ['billing-alerts'] });
    },
  });

  const alerts = data?.alerts || [];
  const thresholds = data?.thresholds || [50, 80, 95, 100];

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.36, ease: [0.16, 1, 0.3, 1] }}
      className="ig-elevated p-5"
      data-testid="billing-alerts-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <Eyebrow>Quota monitoring</Eyebrow>
          <h2 className="ig-display text-[20px] text-nexus-text-primary mt-1 flex items-center gap-2">
            <Bell size={15} className="text-nexus-accent-blue" /> Usage-based Alerts
          </h2>
          <p className="text-[12.5px] text-nexus-text-secondary mt-1.5 max-w-[620px]">
            You'll be notified when your tenant crosses any of these thresholds in a billing cycle.
            Each band fires <span className="ig-mono">once per 30 days</span>.
          </p>
        </div>
        <button
          onClick={() => checkMut.mutate()}
          disabled={checkMut.isPending}
          className="h-9 px-3.5 rounded-lg text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors"
          style={{ background: 'var(--layer-2)', border: '1px solid var(--hairline)', color: 'var(--nexus-text-primary)' }}
          data-testid="billing-check-now"
        >
          <RefreshCw size={11} className={checkMut.isPending ? 'animate-spin' : ''} /> Check now
        </button>
      </div>

      {/* Threshold bar */}
      <div className="flex flex-wrap gap-2 mb-6">
        {thresholds.map((t) => {
          const crossed = alerts.find((a) => a.threshold === t);
          const st = BAND_STYLES[t] || BAND_STYLES[50];
          return (
            <div
              key={t}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium"
              style={{
                background: crossed ? st.bg : 'var(--layer-2)',
                border: `1px solid ${crossed ? st.border : 'var(--hairline)'}`,
                color: crossed ? st.color : 'var(--nexus-text-tertiary)',
              }}
            >
              {crossed ? <TriangleAlert size={10} /> : <Check size={10} />}
              {t}% · {st.label}
            </div>
          );
        })}
      </div>

      {/* Config form */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">
        <div className="md:col-span-3">
          <label className="ig-eyebrow block mb-1.5">Webhook URL (optional)</label>
          <div className="flex items-center gap-2 rounded-lg px-2.5"
               style={{ background: 'var(--nexus-input-bg)', border: '1px solid var(--hairline)' }}>
            <Webhook size={13} className="text-nexus-text-tertiary flex-shrink-0" />
            <input
              value={webhook}
              onChange={(e) => setWebhook(e.target.value)}
              type="url"
              placeholder="https://your-team.slack.com/hooks/…"
              className="flex-1 bg-transparent outline-none py-2 text-[13px] text-nexus-text-primary placeholder:text-nexus-text-tertiary"
              data-testid="billing-webhook-input"
            />
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="ig-eyebrow block mb-1.5">Email</label>
          <label className="flex items-center gap-3 h-[38px] px-3 rounded-lg cursor-pointer"
                 style={{ background: 'var(--nexus-input-bg)', border: '1px solid var(--hairline)' }}>
            <Mail size={13} className="text-nexus-text-tertiary" />
            <span className="text-[13px] text-nexus-text-primary flex-1">Billing email</span>
            <input
              type="checkbox"
              checked={emailOn}
              onChange={(e) => setEmailOn(e.target.checked)}
              className="accent-nexus-accent-mint w-4 h-4"
              data-testid="billing-email-toggle"
            />
          </label>
        </div>
      </div>

      <div className="flex items-center justify-end mb-6">
        <button
          onClick={() => saveMut.mutate({ webhook_url: webhook || null, email_notifications: emailOn })}
          disabled={saveMut.isPending}
          className="ig-cta h-9 px-4 rounded-lg text-[12.5px] font-medium inline-flex items-center gap-1.5"
          style={{ background: '#1E4E8C', color: '#E6EEF9', boxShadow: 'var(--tinted-shadow-cta)' }}
          data-testid="billing-save-config"
        >
          {saveMut.isPending ? 'Saving…' : 'Save preferences'}
        </button>
      </div>

      {/* Alerts log */}
      <div>
        <Eyebrow>Recent alerts · last 90 days</Eyebrow>
        {isLoading ? (
          <div className="ig-skeleton h-16 mt-3" />
        ) : alerts.length === 0 ? (
          <div className="mt-3 text-center py-8 rounded-xl"
               style={{ background: 'var(--layer-2)', border: '1px dashed var(--hairline)' }}>
            <Check size={18} className="mx-auto text-nexus-accent-blue mb-2" />
            <p className="text-[13px] text-nexus-text-primary font-medium">All clear</p>
            <p className="text-[11.5px] text-nexus-text-tertiary mt-0.5">No quota thresholds have been crossed recently.</p>
          </div>
        ) : (
          <div className="mt-3 divide-y" style={{ borderColor: 'var(--hairline)' }}>
            {alerts.map((a) => {
              const st = BAND_STYLES[a.threshold] || BAND_STYLES[50];
              return (
                <div key={a.id}
                     className="flex items-center gap-3 py-3"
                     data-testid={`alert-${a.id}`}>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-medium flex-shrink-0"
                        style={{ background: st.bg, border: `1px solid ${st.border}`, color: st.color }}>
                    <TriangleAlert size={9} /> {a.threshold}% · {st.label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-nexus-text-primary truncate">{a.message}</div>
                    <div className="ig-mono text-[10.5px] text-nexus-text-tertiary mt-0.5">
                      {new Date(a.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                      · plan {a.plan_tier}
                    </div>
                  </div>
                  <span className="ig-numeric text-[11px] text-nexus-text-tertiary">{a.percent_used}%</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </motion.section>
  );
};

export default BillingAlertsPanel;
